import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ICON_CONFIG } from '../../../utils/constants';
import { apiHelpers } from '../../../services/api';

const GridContainer = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  justify-items: center;
  margin: ${({ theme }) => theme.spacing.lg} 0;
  
  /* Desktop: 4 columns */
  grid-template-columns: repeat(${ICON_CONFIG.COLUMNS.DESKTOP}, 1fr);
  
  /* Tablet: 3 columns */
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(${ICON_CONFIG.COLUMNS.TABLET}, 1fr);
  }
  
  /* Mobile: 2 columns */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(${ICON_CONFIG.COLUMNS.MOBILE}, 1fr);
  }
  
  /* Small mobile: 1 column */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(${ICON_CONFIG.COLUMNS.SMALL_MOBILE}, 1fr);
  }
`;

const IconItem = styled.div`
  text-align: center;
  max-width: ${ICON_CONFIG.WIDTH}px;
`;

const IconLinkInternal = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    text-decoration: none;
    
    img {
      transform: scale(1.05);
    }
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const IconLinkExternal = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    text-decoration: none;
    
    img {
      transform: scale(1.05);
    }
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const IconContent = styled.div`
  display: block;
  color: inherit;
  opacity: 0.7; /* Slightly dimmed to indicate it's not clickable */
`;

const IconImage = styled.img`
  width: ${ICON_CONFIG.WIDTH}px;
  height: ${ICON_CONFIG.HEIGHT}px;
  object-fit: contain;
  transition: ${({ theme }) => theme.transitions.fast};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 120px;
    height: 152px;
  }
`;

const IconTitle = styled.h3`
  font-size: 1rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

interface IconGridItem {
  title: string;
  image: string;
  link: string | null;
  alt: string;
  external?: boolean;
}

interface IconGridProps {
  items: IconGridItem[];
  className?: string;
}

export const IconGrid: React.FC<IconGridProps> = ({ items, className }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <GridContainer className={className}>
      {items.map((item, index) => {
        const isExternal = item.external || apiHelpers.isExternalUrl(item.link);
        const hasLink = item.link !== null && item.link !== undefined;
        
        return (
          <IconItem key={`icon-${index}-${item.title.replace(/\s+/g, '-').toLowerCase()}`}>
            {!hasLink ? (
              // No link - just display as static content
              <IconContent>
                <IconImage 
                  src={apiHelpers.buildAssetUrl(item.image)} 
                  alt={item.alt} 
                  loading="lazy"
                />
                <IconTitle>{item.title}</IconTitle>
              </IconContent>
            ) : isExternal ? (
              <IconLinkExternal 
                href={item.link!} 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label={`${item.title} (opens in new window)`}
              >
                <IconImage 
                  src={apiHelpers.buildAssetUrl(item.image)} 
                  alt={item.alt} 
                  loading="lazy"
                />
                <IconTitle>{item.title}</IconTitle>
              </IconLinkExternal>
            ) : (
              <IconLinkInternal to={item.link!}>
                <IconImage 
                  src={apiHelpers.buildAssetUrl(item.image)} 
                  alt={item.alt} 
                  loading="lazy"
                />
                <IconTitle>{item.title}</IconTitle>
              </IconLinkInternal>
            )}
          </IconItem>
        );
      })}
    </GridContainer>
  );
};

export default IconGrid;
